// src/utils/authContext.js

import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { auth } from "../config/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

// ✅ Create Authentication Context
const AuthContext = createContext(null);

/**
 * **useAuth Hook** - Provides authentication state.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    console.warn("⚠️ useAuth must be used within an AuthProvider.");
    return { user: null, loading: true };
  }
  return context;
};

/**
 * **AuthProvider Component** - Wraps children components with authentication context.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const nullAuthTimeoutRef = useRef(null);

  const refreshUser = useCallback(async () => {
    try {
      const current = auth.currentUser;
      if (!current) {
        setUser(null);
        return null;
      }
      await current.reload();
      const refreshed = auth.currentUser;
      if (refreshed) {
        setUser({
          uid: refreshed.uid,
          email: refreshed.email,
          emailVerified: refreshed.emailVerified,
        });
      }
      return refreshed;
    } catch (error) {
      console.error("❌ Error refreshing auth state:", error?.code || "auth_error");
      return null;
    }
  }, []);

  const handleAuthChange = useCallback(
    (authUser) => {
      try {
        if (authUser) {
          if (nullAuthTimeoutRef.current) {
            clearTimeout(nullAuthTimeoutRef.current);
            nullAuthTimeoutRef.current = null;
          }
          console.log("✅ User authenticated");
          setUser({ uid: authUser.uid, email: authUser.email, emailVerified: authUser.emailVerified });

          if (!authUser.emailVerified) {
            console.info("📭 Email not verified. Waiting for user-initiated resend.");
          } else {
            console.log("📨 Email is already verified.");
          }
        } else {
          if (auth.currentUser) {
            return;
          }
          // small delay to avoid false negatives; cancel if auth arrives
          if (nullAuthTimeoutRef.current) {
            clearTimeout(nullAuthTimeoutRef.current);
          }
          nullAuthTimeoutRef.current = setTimeout(() => {
            if (auth.currentUser) {
              nullAuthTimeoutRef.current = null;
              return;
            }
            setUser(null);
            nullAuthTimeoutRef.current = null;
          }, 250);
        }
      } catch (error) {
        console.error("❌ Error handling auth state:", error?.code || "auth_error");
      } finally {
        setLoading(false);
      }
    },
    [setUser, setLoading]
  );

  // Subscribe to Firebase auth state
  useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, handleAuthChange);
    return () => {
      if (nullAuthTimeoutRef.current) {
        clearTimeout(nullAuthTimeoutRef.current);
        nullAuthTimeoutRef.current = null;
      }
      unsubscribe();
    };
  }, [handleAuthChange]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, setLoading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
